import { Link } from "react-router-dom";
import { Cloud } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="flex items-center gap-2">
            <Cloud className="w-8 h-8 text-bronze" />
            <span className="text-xl font-bold text-midnight font-outfit">CloudSync Pro</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-midnight font-playfair mb-2">Terms of Service</h1>
        <p className="text-slate-500 mb-8">Last updated: January 2025</p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">1. Agreement to Terms</h2>
            <p className="text-slate-600 mb-4">
              By purchasing, downloading, installing, or using CloudSync Pro ("the Plugin"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Plugin.
            </p>
            <p className="text-slate-600">
              These Terms constitute a legally binding agreement between you and Sebastian Alliance Group ("Company," "we," "us," or "our").
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">2. License Grant</h2>
            
            <h3 className="text-lg font-semibold text-midnight mt-4 mb-2">2.1 License Scope</h3>
            <p className="text-slate-600 mb-4">
              Upon purchase, we grant you a limited, non-exclusive, non-transferable license to use CloudSync Pro on <strong>ONE (1) WooCommerce website</strong> that you own or operate.
            </p>

            <h3 className="text-lg font-semibold text-midnight mt-4 mb-2">2.2 License Restrictions</h3>
            <p className="text-slate-600 mb-4">You may NOT:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Use one license key on multiple websites</li>
              <li>Share, sell, or distribute your license key to others</li>
              <li>Modify, reverse engineer, or decompile the Plugin</li>
              <li>Remove or alter any proprietary notices or labels</li>
              <li>Use the Plugin for illegal purposes</li>
              <li>Sublicense, rent, or lease the Plugin</li>
            </ul>

            <h3 className="text-lg font-semibold text-midnight mt-4 mb-2">2.3 License Transfer</h3>
            <p className="text-slate-600">
              You may deactivate your license from one website and reactivate it on a different website you own through your account dashboard. Only one active installation is permitted per license at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">3. Pricing and Payment</h2>
            
            <h3 className="text-lg font-semibold text-midnight mt-4 mb-2">3.1 Plugin Purchase</h3>
            <p className="text-slate-600 mb-4">
              The Plugin is available for a one-time purchase price of <strong>$119.00 USD</strong>. This includes lifetime access to the Plugin and updates.
            </p>

            <h3 className="text-lg font-semibold text-midnight mt-4 mb-2">3.2 Transaction Fee</h3>
            <p className="text-slate-600 mb-4">
              In addition to the purchase price, a <strong>2% transaction fee</strong> applies to all bookings processed through the Plugin. This fee is calculated on the total booking amount and is used to maintain our infrastructure and services.
            </p>

            <h3 className="text-lg font-semibold text-midnight mt-4 mb-2">3.3 Payment Processing</h3>
            <p className="text-slate-600 mb-4">
              All payments are processed by <strong>Sebastian Alliance Group</strong>. This name will appear on your credit card or bank statement. We use secure payment processors including Stripe, Scotiabank, Bank of the Bahamas, and Visa networks.
            </p>

            <h3 className="text-lg font-semibold text-midnight mt-4 mb-2">3.4 Taxes</h3>
            <p className="text-slate-600">
              Prices do not include applicable taxes. You are responsible for paying all taxes associated with your purchase.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">4. Refund Policy</h2>
            <p className="text-slate-600 mb-4">
              We offer a <strong>30-day money-back guarantee</strong>. If you are not satisfied with CloudSync Pro within 30 days of purchase, you may request a full refund.
            </p>
            <p className="text-slate-600 mb-4">To request a refund:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Contact our support team within 30 days of purchase</li>
              <li>Provide your order details and reason for refund</li>
              <li>Deactivate and uninstall the Plugin from your website</li>
            </ul>
            <p className="text-slate-600 mt-4">
              Refunds do not apply to the 2% transaction fees already incurred on processed bookings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">5. Plugin Functionality</h2>
            
            <h3 className="text-lg font-semibold text-midnight mt-4 mb-2">5.1 Features</h3>
            <p className="text-slate-600 mb-4">CloudSync Pro provides:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Two-way synchronization between Cloudbeds PMS and WooCommerce</li>
              <li>Room availability and pricing sync</li>
              <li>Booking/reservation management</li>
              <li>Guest information synchronization</li>
              <li>Support for Scotiabank, Bank of the Bahamas, and Visa payment processors</li>
            </ul>

            <h3 className="text-lg font-semibold text-midnight mt-4 mb-2">5.2 Requirements</h3>
            <p className="text-slate-600 mb-4">To use the Plugin, you must have:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>An active Cloudbeds account with API access</li>
              <li>WordPress 5.0 or higher</li>
              <li>WooCommerce 4.0 or higher</li>
              <li>PHP 7.4 or higher</li>
              <li>SSL certificate installed on your website</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">6. Data and Privacy</h2>
            <p className="text-slate-600 mb-4">
              By using the Plugin, you acknowledge that booking and transaction data will be transmitted to our servers for:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>License validation</li>
              <li>Transaction fee calculation</li>
              <li>Synchronization services</li>
              <li>Support and troubleshooting</li>
            </ul>
            <p className="text-slate-600 mt-4">
              Please review our <Link to="/privacy" className="text-bronze hover:underline">Privacy Policy</Link> for detailed information on data handling.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">7. Intellectual Property</h2>
            <p className="text-slate-600 mb-4">
              CloudSync Pro, including all code, design, documentation, and trademarks, is the exclusive property of Sebastian Alliance Group. Your license does not grant you any ownership rights.
            </p>
            <p className="text-slate-600">
              Cloudbeds, WooCommerce, WordPress, Scotiabank, Bank of the Bahamas, and Visa are trademarks of their respective owners.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">8. Disclaimer of Warranties</h2>
            <p className="text-slate-600 mb-4">
              THE PLUGIN IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="text-slate-600">
              We do not warrant that the Plugin will be uninterrupted, error-free, or completely secure. We are not responsible for any issues arising from third-party services (Cloudbeds, WooCommerce, payment processors).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">9. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, SEBASTIAN ALLIANCE GROUP SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Loss of profits or revenue</li>
              <li>Loss of bookings or reservations</li>
              <li>Data loss or corruption</li>
              <li>Business interruption</li>
              <li>Any damages arising from third-party service failures</li>
            </ul>
            <p className="text-slate-600 mt-4">
              Our total liability shall not exceed the amount paid for the Plugin license.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">10. Indemnification</h2>
            <p className="text-slate-600">
              You agree to indemnify and hold harmless Sebastian Alliance Group from any claims, damages, losses, or expenses arising from your use of the Plugin, violation of these Terms, or infringement of any third-party rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">11. License Termination</h2>
            <p className="text-slate-600 mb-4">We may terminate or suspend your license if you:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Violate these Terms</li>
              <li>Use the Plugin on multiple unauthorized websites</li>
              <li>Engage in fraudulent activity</li>
              <li>Fail to pay applicable transaction fees</li>
              <li>Attempt to circumvent license restrictions</li>
            </ul>
            <p className="text-slate-600 mt-4">
              Upon termination, you must immediately stop using the Plugin and delete all copies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">12. Updates and Support</h2>
            <p className="text-slate-600 mb-4">
              Your license includes access to Plugin updates and email support. We reserve the right to modify, suspend, or discontinue support at any time.
            </p>
            <p className="text-slate-600">
              Updates may be required to maintain compatibility with Cloudbeds API changes, WooCommerce updates, or security patches.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">13. Governing Law</h2>
            <p className="text-slate-600">
              These Terms shall be governed by and construed in accordance with applicable laws. Any disputes shall be resolved through binding arbitration or in courts of competent jurisdiction.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">14. Changes to Terms</h2>
            <p className="text-slate-600">
              We reserve the right to modify these Terms at any time. Changes will be posted on this page with an updated date. Continued use of the Plugin after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-midnight font-outfit mb-4">15. Contact Information</h2>
            <p className="text-slate-600">
              For questions about these Terms, please contact us at:
            </p>
            <p className="text-slate-600 mt-2">
              <strong>Email:</strong> legal@cloudsync.pro<br />
              <strong>Company:</strong> Sebastian Alliance Group
            </p>
          </section>

          <section className="mb-8 p-6 bg-slate-50 rounded-lg">
            <h2 className="text-xl font-semibold text-midnight font-outfit mb-4">Summary of Key Points</h2>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>License:</strong> One license = One website only</li>
              <li><strong>Price:</strong> $119 one-time + 2% per booking transaction</li>
              <li><strong>Payments by:</strong> Sebastian Alliance Group</li>
              <li><strong>Refund:</strong> 30-day money-back guarantee</li>
              <li><strong>Requirements:</strong> Cloudbeds API access, WordPress 5.0+, WooCommerce 4.0+</li>
            </ul>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200">
          <Link to="/" className="text-bronze hover:underline">← Back to Home</Link>
        </div>
      </main>
    </div>
  );
}
